import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  getResolvedAuth,
  getToken,
  loginWithRedirect,
  logout,
  ResolvedAuth,
  whenAuthReady,
} from '../lib/auth';
import { PermissionString } from '../utils/permissions';

export type { ResolvedAuth } from '../lib/auth';

interface AuthContextType {
  permissions: string[];
  hasPermission: (permission: PermissionString) => boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  getToken: () => Promise<string | null>;
  loginWithRedirect: (options?: { appState?: { returnTo?: string } }) => void;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  /** Resolves once the auth bootstrap completes, with the settled auth state. */
  whenReady: () => Promise<ResolvedAuth>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Adapts the module-level auth state to the hook interface components
 * consume. The auth bootstrap completes before the app renders, so the value
 * is a settled snapshot: no loading states, no effects.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const value = useMemo<AuthContextType>(() => {
    const resolved = getResolvedAuth();
    return {
      permissions: resolved.permissions,
      hasPermission: resolved.hasPermission,
      isLoading: false,
      isAuthenticated: resolved.isAuthenticated,
      getToken,
      loginWithRedirect: (options) => {
        void loginWithRedirect(options?.appState?.returnTo);
      },
      logout: (options) => {
        void logout(options?.logoutParams?.returnTo);
      },
      whenReady: whenAuthReady,
    };
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
